/**
 * Dead band (px) between "stops fitting" and "fits again". Without it a bar whose width sits right on the container
 * width could collapse and restore a label on every layout pass (sub-pixel rounding, a scrollbar appearing).
 */
export const STEP_BAR_HYSTERESIS_PX = 8;

export interface StepBarItem {
  /** Width (px) of the step with its label and chevron, as in the full bar. */
  fullWidth: number;
  /** Width (px) of the step as an icon only. */
  compactWidth: number;
  /** The current step always keeps its label. */
  isCurrent: boolean;
}

/**
 * Indices of the steps that may turn into icons, in the order they do: the rightmost step first, working leftwards,
 * skipping the current step.
 */
export const getCollapseOrder = (items: Pick<StepBarItem, 'isCurrent'>[]): number[] =>
  items
    .map((_, index) => index)
    .filter((index) => !items[index].isCurrent)
    .reverse();

/** Per step, in display order: true when the step shows as an icon only. */
export const getCollapsedSteps = (items: Pick<StepBarItem, 'isCurrent'>[], collapsedCount: number): boolean[] => {
  const collapsed = items.map(() => false);

  getCollapseOrder(items)
    .slice(0, Math.max(0, collapsedCount))
    .forEach((index) => {
      collapsed[index] = true;
    });

  return collapsed;
};

/** Width (px) of the bar on one row with `collapsedCount` steps collapsed. `extraWidth` is the menu's own padding/border. */
export const getStepBarWidth = (items: StepBarItem[], collapsedCount: number, extraWidth = 0): number => {
  const collapsed = getCollapsedSteps(items, collapsedCount);
  const total = items.reduce((sum, item, index) => sum + (collapsed[index] ? item.compactWidth : item.fullWidth), extraWidth);

  // Round up so sub-pixel text widths can never make a bar that is 0.4px too wide count as fitting.
  return Math.ceil(total - 0.001);
};

interface DecideCollapsedCountArgs {
  /** Width the bar may use (px). */
  containerWidth: number;
  /** Measured widths of every step, in display order. */
  items: StepBarItem[];
  /** How many steps are icons on screen right now. */
  collapsedCountNow: number;
  extraWidth?: number;
  hysteresis?: number;
}

/**
 * How many steps show as icons (MDSI1-4952 D5). Pure, so it can be tested without a browser.
 *
 * - The fewest steps collapse that let the bar fit on one row, so as many steps as possible keep their labels.
 * - Steps collapse one at a time, rightmost first, and the current step never does (`getCollapseOrder`).
 * - Shrinking collapses as soon as the bar no longer fits. Widening restores a label only once the wider bar fits with
 *   `hysteresis` px to spare, so a container that jitters around a threshold cannot flap.
 * - If even the all-icons bar does not fit, every collapsible step is collapsed and the bar wraps onto a second row.
 * - If the container or a step has not been measured yet (not laid out, display: none), the count on screen is kept.
 */
export const decideCollapsedCount = ({ containerWidth, items, collapsedCountNow, extraWidth = 0, hysteresis = STEP_BAR_HYSTERESIS_PX }: DecideCollapsedCountArgs): number => {
  const maxCount = getCollapseOrder(items).length;
  const now = Math.min(Math.max(0, Math.floor(collapsedCountNow) || 0), maxCount);
  const isMeasured = containerWidth > 0 && items.length > 0 && items.every((item) => item.fullWidth > 0 && item.compactWidth > 0);

  if (!isMeasured) return now;

  const widthAt = (count: number): number => getStepBarWidth(items, count, extraWidth);

  if (widthAt(now) > containerWidth) {
    for (let count = now + 1; count <= maxCount; count++) {
      if (widthAt(count) <= containerWidth) return count;
    }

    return maxCount;
  }

  for (let count = 0; count < now; count++) {
    if (widthAt(count) + hysteresis <= containerWidth) return count;
  }

  return now;
};
